// Enfermedad Actual (ficha 360°): pantalla Diagnósticos estilo reliv.
// - Historial de consultas raíz (episodios) a la izquierda; detalle a la derecha.
// - Cada episodio tiene EVOLUCIONES estructuradas numeradas (signos propios +
//   FUM/perímetro, texto libre o SOAP, CIE propio) — evolucion.episodio_id.
// - ALTA cierra el episodio (no admite más evoluciones); se puede deshacer.
// - Generar: Receta | Pedido de laboratorio | Certificado precargados con el dx.
// - Agregar CIE: typeahead + modal paginado con FAVORITOS por usuario.
// - Curvas de Crecimiento (percentiles CDC reales + mediciones del paciente).
// Regla de edición: sin firmar y <24h (script 375/376).
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, ChevronDown, FileSignature, Loader2, Lock,
  LogOut, Pencil, Plus, Printer, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import {
  AutoTextarea, Campo, clasificacionIMCEdad, esSchema, fieldCls, med, medRpc, mesesEntre, useRefIMC,
} from './shared';
import type { Convenio, Diagnostico, EvolucionItem, Historia, Plantilla, SignoVital, SoapContenido } from './shared';
import { FormularioClinico, calcIMC } from './FormularioClinico';
import { NuevaReceta } from './Recetas';
import { NuevoCertificado } from './Certificados';
import { NuevaSolicitud } from './Laboratorio';
import { edadAl } from './EsquemaVacunacion';
import { CurvasCrecimiento } from './CurvasCrecimiento';
import { DiagnosticosCie } from './DiagnosticosCie';
import { useVoiceForm, type CampoVoz } from '@/contexts/VoiceFormContext';
import { LOCALE } from '@/lib/utils/regional';
export { DiagnosticosCie } from './DiagnosticosCie';

// to_jsonb(ev) trae más columnas que el tipo compartido
type EvPlus = EvolucionItem['evolucion'] & {
  created_at?: string; medico_id?: string; especialidad_id?: string | null;
  convenio_id?: string | null; medico_derivador_id?: string | null;
  episodio_id?: string | null; alta_at?: string | null; alta_motivo?: string | null;
};

const HORAS_EDICION = 24;
const esEditable = (ev: EvPlus) =>
  !ev.firmada && !!ev.created_at && Date.now() - new Date(ev.created_at).getTime() < HORAS_EDICION * 3600e3;

const sigVacio: SignoVital = {
  presion_sistolica: null, presion_diastolica: null, frecuencia_cardiaca: null, frecuencia_resp: null,
  temperatura: null, spo2: null, peso: null, talla: null, glucosa: null, dolor: null, observaciones: null,
  fum: null, perimetro_cefalico: null,
};

const fmtFecha = (v?: string | null) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' });
};

interface NotaSeguimiento { fecha: string; usuario: string; texto: string }
const notasDe = (ev: EvPlus): NotaSeguimiento[] =>
  Array.isArray(ev.datos?.notas_seguimiento) ? (ev.datos!.notas_seguimiento as NotaSeguimiento[]) : [];
const examenesDe = (ev: EvPlus): string =>
  typeof ev.datos?.examenes_complementarios === 'string' ? (ev.datos.examenes_complementarios as string) : '';
const textoEvolucionDe = (ev: EvPlus): string =>
  typeof ev.datos?.texto_evolucion === 'string' ? (ev.datos.texto_evolucion as string) : '';

// Valida signos según reliv: peso ≤ 300 kg, talla ≤ 2.50 m (250 cm)
const validarSignos = (s: SignoVital): string | null => {
  if (s.peso != null && s.peso > 300) return 'El valor del peso no puede ser mayor de 300';
  if (s.talla != null && s.talla > 250) return 'El valor de la talla no puede ser mayor de 2.50 m (250 cm)';
  return null;
};

export function EnfermedadActual({ pacienteId, nombre, fechaNacimiento, genero, onVolver }: {
  pacienteId: string; nombre: string; fechaNacimiento: string | null; genero: string | null; onVolver: () => void;
}) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: historia, isLoading } = useQuery<Historia>({
    queryKey: ['med-ficha', pacienteId, 'historia'],
    queryFn: () => medRpc<Historia>('med_historia', { _paciente_id: pacienteId }),
  });
  const { data: refIMC } = useRefIMC(genero);
  const clasifIMC = (imc: number | null, fecha?: string | null) =>
    clasificacionIMCEdad(imc, mesesEntre(fechaNacimiento, fecha ?? new Date().toISOString()), refIMC);
  const todas = historia?.evoluciones ?? [];
  // consultas raíz (episodios) y sus evoluciones hijas
  const raices = todas.filter((e) => !(e.evolucion as EvPlus).episodio_id);
  const hijasDe = (rootId: string) => todas
    .filter((e) => (e.evolucion as EvPlus).episodio_id === rootId)
    .sort((a, b) => ((a.evolucion as EvPlus).created_at ?? a.evolucion.fecha) < ((b.evolucion as EvPlus).created_at ?? b.evolucion.fecha) ? -1 : 1);
  const refrescar = () => void qc.invalidateQueries({ queryKey: ['med-ficha', pacienteId] });

  const [selId, setSelId] = useState<string | null>(null);
  const [form, setForm] = useState<{ item: EvolucionItem | null } | null>(null);      // consulta raíz
  const [formEvo, setFormEvo] = useState<{ item: EvolucionItem | null } | null>(null); // evolución hija
  const [genMenu, setGenMenu] = useState(false);
  const [gen, setGen] = useState<'receta' | 'pedido' | 'certificado' | null>(null);
  const [curvas, setCurvas] = useState(false);

  const sel = raices.find((e) => e.evolucion.id === selId) ?? raices[0] ?? null;
  const evolsSel = sel ? hijasDe(sel.evolucion.id) : [];
  const numConsulta = (item: EvolucionItem) => raices.length - raices.findIndex((e) => e.evolucion.id === item.evolucion.id);
  const tituloConsulta = (item: EvolucionItem) =>
    `${numConsulta(item) === 1 ? 'Primera Consulta' : `Consulta ${numConsulta(item)}`} ${fmtFecha(item.evolucion.fecha)}`;

  const firmar = useMutation({
    mutationFn: (id: string) => medRpc('med_firmar_evolucion', { _id: id }),
    onSuccess: () => { toast.success('Firmada'); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const { isAdmin } = usePermissions();
  const alta = useMutation({
    mutationFn: ({ id, deshacer, motivo }: { id: string; deshacer: boolean; motivo?: string | null }) =>
      medRpc('med_episodio_alta', { _id: id, _motivo: deshacer ? null : (motivo || null), _deshacer: deshacer }),
    onSuccess: (_d, v) => { toast.success(v.deshacer ? 'Alta revertida' : 'Paciente dado de ALTA del episodio'); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const dxPrincipal = sel?.diagnosticos?.find((d) => d.es_principal) ?? sel?.diagnosticos?.[0];

  const imprimir = () => {
    if (!sel) return;
    const esc = (v: unknown) => String(v ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
    const campo = (l: string, v?: string | null) => (v ? `<h3>${l}</h3><p>${esc(v)}</p>` : '');
    const sigHtml = (s?: SignoVital) => s ? `<p class="sig"><b>FC:</b> ${esc(s.frecuencia_cardiaca ?? '—')} · <b>FR:</b> ${esc(s.frecuencia_resp ?? '—')} · <b>SO2:</b> ${esc(s.spo2 ?? '—')} · <b>TA:</b> ${s.presion_sistolica != null ? `${s.presion_sistolica}/${s.presion_diastolica ?? '—'}` : '—'} · <b>T°:</b> ${esc(s.temperatura ?? '—')} · <b>Peso:</b> ${esc(s.peso ?? '—')} kg · <b>Talla:</b> ${esc(s.talla ?? '—')} · <b>IMC:</b> ${esc(calcIMC(s.peso, s.talla) ?? '—')}${s.fum ? ` · <b>FUM:</b> ${esc(fmtFecha(s.fum))}` : ''}${s.perimetro_cefalico != null ? ` · <b>PC:</b> ${esc(s.perimetro_cefalico)} cm` : ''}</p>` : '';
    const dxHtml = (dx?: Diagnostico[]) => (dx?.length ? `<table><thead><tr><th>Código</th><th>CIE-10</th><th class="c">Presuntivo</th><th class="c">Definitivo</th><th>Descripción</th></tr></thead><tbody>${dx.map((d) => `<tr><td>${esc(d.cie10_codigo ?? '—')}</td><td>${esc(d.descripcion)}</td><td class="c">${d.tipo === 'presuntivo' ? '●' : ''}</td><td class="c">${d.tipo === 'definitivo' ? '●' : ''}</td><td>${d.es_principal ? 'Principal' : ''}</td></tr>`).join('')}</tbody></table>` : '');
    const ev = sel.evolucion as EvPlus;
    const evolsHtml = evolsSel.map((h, i) => {
      const he = h.evolucion as EvPlus;
      return `<hr><h2>EVOLUCIÓN # ${i + 1} — ${esc(new Date(he.fecha).toLocaleString(LOCALE))} · ${esc(h.medico)}</h2>
        ${sigHtml(h.signos?.[0])}${campo('Evolución', textoEvolucionDe(he))}
        ${campo('Subjetivo', he.subjetivo)}${campo('Objetivo', he.objetivo)}${campo('Análisis', he.analisis)}${campo('Plan', he.plan)}
        ${dxHtml(h.diagnosticos)}${campo('Observaciones', he.observaciones)}`;
    }).join('');
    const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Enfermedad actual</title>
      <style>*{font-family:Arial,Helvetica,sans-serif}h1{font-size:16px;margin:0 0 2px}h2{font-size:13px;margin:8px 0 2px}.sub{font-size:12px;color:#555;margin:0 0 10px}
      h3{font-size:12px;margin:10px 0 2px;text-transform:uppercase;color:#333}p{font-size:12px;margin:0;white-space:pre-wrap}
      table{border-collapse:collapse;width:100%;font-size:11px;margin-top:4px}th,td{border:1px solid #ccc;padding:3px 6px;text-align:left}
      th{background:#f0f0f0}.c{text-align:center}hr{border:none;border-top:1px solid #ccc;margin:10px 0}
      .sig{font-size:12px;margin:6px 0}@media print{@page{margin:12mm}}</style></head><body>
      <h1>${esc(tituloConsulta(sel))}${ev.alta_at ? ' — DADO DE ALTA' : ''}</h1>
      <p class="sub">Paciente: <b>${esc(nombre)}</b> · Edad a la consulta: ${esc(edadAl(fechaNacimiento, ev.fecha))} · Médico: ${esc(sel.medico)}${sel.especialidad ? ` · ${esc(sel.especialidad)}` : ''}</p>
      ${sigHtml(sel.signos?.[0])}
      ${campo('Motivo de consulta', ev.motivo)}${campo('Subjetivo - Anamnesis', ev.subjetivo)}${campo('Objetivo - Examen físico', ev.objetivo)}
      ${campo('Exámenes complementarios', examenesDe(ev))}${campo('Análisis - Diagnóstico', ev.analisis)}
      ${dxHtml(sel.diagnosticos)}
      ${campo('Plan - Tratamiento', ev.plan)}${campo('Observaciones', ev.observaciones)}
      ${ev.alta_at ? campo('Alta', `${new Date(ev.alta_at).toLocaleString(LOCALE)}${ev.alta_motivo ? ` — ${ev.alta_motivo}` : ''}`) : ''}
      ${evolsHtml}
      <script>window.onload=function(){window.print();}</script></body></html>`;
    const win = window.open('', '_blank');
    if (!win) { toast.error('Permite las ventanas emergentes para imprimir'); return; }
    win.document.write(html); win.document.close();
  };

  if (isLoading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;

  return (
    <div className="space-y-3">
      {dlg.ui}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onVolver}><ArrowLeft className="w-4 h-4 mr-1" /> Volver a la ficha</Button>
        <h2 className="text-lg font-semibold">Enfermedad Actual</h2>
      </div>

      <section>
        <div className="flex items-center justify-between rounded-t-md bg-primary/10 px-4 py-2">
          <span className="text-sm font-semibold">Diagnósticos</span>
          {!form && !formEvo && <Button size="sm" onClick={() => setForm({ item: null })}><Plus className="w-4 h-4 mr-1" /> Nueva consulta</Button>}
        </div>
        <div className="rounded-b-md border border-t-0 bg-card p-4 space-y-3">
          <p className="rounded-md bg-primary/10 px-3 py-2 text-xs">
            ℹ️ Estimado Dr/a: le recordamos que las consultas se podrán modificar únicamente hasta {HORAS_EDICION} horas posterior a su creación (y quedan bloqueadas al firmarse).
          </p>

          <div className="flex gap-4 items-start">
            {/* Historial de episodios */}
            <div className="w-48 shrink-0 rounded-md border">
              <div className="border-b bg-muted px-3 py-1.5 text-xs font-semibold text-muted-foreground">Historial</div>
              <div className="max-h-80 overflow-y-auto">
                {raices.length === 0 && <p className="px-3 py-3 text-xs text-muted-foreground">Sin consultas</p>}
                {raices.map((e) => {
                  const ev = e.evolucion as EvPlus;
                  const n = hijasDe(ev.id).length;
                  return (
                    <button key={ev.id} type="button"
                      onClick={() => { setSelId(ev.id); setForm(null); setFormEvo(null); }}
                      className={`block w-full border-b px-3 py-1.5 text-left text-sm last:border-0 hover:bg-muted ${sel?.evolucion.id === ev.id && !form && !formEvo ? 'bg-primary/10 font-medium' : ''}`}>
                      {fmtFecha(ev.fecha)}
                      {n > 0 && <span className="ml-1 text-xs text-muted-foreground">+{n}</span>}
                      {ev.firmada && <Lock className="inline w-3 h-3 ml-1 text-muted-foreground" />}
                      {ev.alta_at && <span className="ml-1 rounded bg-emerald-100 px-1 text-[10px] text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">ALTA</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Detalle / formularios */}
            <div className="flex-1 min-w-0">
              {form ? (
                <FormConsulta pacienteId={pacienteId} item={form.item} episodioId={null} genero={genero} clasif={clasifIMC}
                  onCancel={() => setForm(null)}
                  onSaved={(id) => { setForm(null); setSelId(id); refrescar(); }} />
              ) : formEvo && sel ? (
                <FormConsulta pacienteId={pacienteId} item={formEvo.item} episodioId={sel.evolucion.id} esEvolucion genero={genero} clasif={clasifIMC}
                  onCancel={() => setFormEvo(null)}
                  onSaved={() => { setFormEvo(null); refrescar(); }} />
              ) : !sel ? (
                <p className="py-10 text-center text-sm text-primary/80">No existe historial de enfermedades — usa «Nueva consulta»</p>
              ) : (() => {
                const ev = sel.evolucion as EvPlus;
                return (
                  <div className="space-y-3 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="font-semibold">
                          {tituloConsulta(sel)} <span className="font-normal text-muted-foreground">· {sel.medico}{sel.especialidad ? ` · ${sel.especialidad}` : ''}</span>
                          {ev.alta_at && <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" title={ev.alta_motivo ?? ''}>DADO DE ALTA {fmtFecha(ev.alta_at)}</span>}
                        </p>
                        <p className="text-xs text-muted-foreground">Edad a la consulta: <b>{edadAl(fechaNacimiento, ev.fecha)}</b></p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => setCurvas(true)} title="Curvas de crecimiento"><TrendingUp className="w-3.5 h-3.5 mr-1" /> Curvas</Button>
                        <Button size="sm" variant="outline" onClick={imprimir}
                                title="En el diálogo elige «Guardar como PDF» para obtener el archivo">
                          <Printer className="w-3.5 h-3.5 mr-1" /> Imprimir
                        </Button>
                        <div className="relative">
                          <Button size="sm" onClick={() => setGenMenu((o) => !o)}>Generar <ChevronDown className="w-3.5 h-3.5 ml-1" /></Button>
                          {genMenu && (
                            <div className="absolute right-0 z-10 mt-1 w-44 rounded-md border bg-card shadow-lg">
                              {([['receta', 'Receta'], ['pedido', 'Pedido (laboratorio)'], ['certificado', 'Certificado']] as const).map(([v, l]) => (
                                <button key={v} type="button" className="block w-full px-3 py-1.5 text-left text-sm hover:bg-muted" onClick={() => { setGenMenu(false); setGen(v); }}>{l}</button>
                              ))}
                            </div>
                          )}
                        </div>
                        {!ev.alta_at && <Button size="sm" variant="outline" onClick={() => setFormEvo({ item: null })}><Plus className="w-3.5 h-3.5 mr-1" /> Nueva Evolución</Button>}
                        {esEditable(ev) && <Button size="sm" variant="outline" onClick={() => setForm({ item: sel })}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>}
                        {!ev.firmada
                          ? <Button size="sm" variant="outline" isLoading={firmar.isPending} onClick={() => firmar.mutate(ev.id)}><FileSignature className="w-3.5 h-3.5 mr-1" /> Firmar</Button>
                          : <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Lock className="w-3.5 h-3.5" /> Firmada</span>}
                        {ev.alta_at
                          ? (isAdmin()
                              ? <Button size="sm" variant="outline" isLoading={alta.isPending}
                                  onClick={async () => {
                                    if (!(await dlg.confirmar('¿Deshacer el ALTA de este episodio?', { detalle: 'Volverá a admitir evoluciones. Acción reservada a administradores.', peligro: true }))) return;
                                    alta.mutate({ id: ev.id, deshacer: true });
                                  }}>Deshacer alta</Button>
                              : <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400" title="El alta es irreversible; solo un administrador puede deshacerla."><Lock className="w-3.5 h-3.5" /> Dado de alta</span>)
                          : <Button size="sm" variant="outline" className="text-emerald-700" isLoading={alta.isPending}
                              onClick={async () => {
                                const v = await dlg.pedir('Dar de ALTA este episodio', [{ key: 'motivo', label: 'Motivo del alta (opcional)', minimo: 0 }],
                                  { detalle: 'No admitirá más evoluciones. Una vez dado, solo un administrador podrá deshacerlo.', aceptar: 'Dar de alta' });
                                if (v === null) return;
                                alta.mutate({ id: ev.id, deshacer: false, motivo: v.motivo });
                              }}>
                              <LogOut className="w-3.5 h-3.5 mr-1" /> ALTA
                            </Button>}
                      </div>
                    </div>

                    <VistaConsulta item={sel} clasif={clasifIMC} />

                    {/* Evoluciones del episodio */}
                    {evolsSel.map((h, i) => {
                      const he = h.evolucion as EvPlus;
                      return (
                        <div key={he.id} className="rounded-md border">
                          <div className="flex flex-wrap items-center justify-between gap-2 rounded-t-md bg-muted px-3 py-1.5">
                            <span className="text-xs font-semibold">EVOLUCIÓN # {i + 1} — {new Date(he.fecha).toLocaleString(LOCALE)} · {h.medico}</span>
                            <span className="flex items-center gap-2">
                              <button type="button" className="text-xs text-primary hover:underline" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Ver 1era Consulta</button>
                              {esEditable(he) && !ev.alta_at && <button type="button" className="text-xs text-primary hover:underline" onClick={() => setFormEvo({ item: h })}>Editar</button>}
                              {!he.firmada
                                ? <button type="button" className="text-xs text-primary hover:underline" onClick={() => firmar.mutate(he.id)}>Firmar</button>
                                : <Lock className="w-3 h-3 text-muted-foreground" />}
                            </span>
                          </div>
                          <div className="space-y-2 px-3 py-2">
                            <VistaConsulta item={h} esEvolucion clasif={clasifIMC} />
                          </div>
                        </div>
                      );
                    })}

                    {/* Notas simples (addendum) heredadas */}
                    {notasDe(ev).length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold uppercase text-muted-foreground">Notas de seguimiento</h4>
                        <ul className="mt-1 divide-y rounded-md border text-sm">
                          {notasDe(ev).map((x, i) => (
                            <li key={i} className="px-3 py-1.5">
                              <span className="text-xs text-muted-foreground">{new Date(x.fecha).toLocaleString(LOCALE)} · {x.usuario}</span>
                              <p className="whitespace-pre-wrap">{x.texto}</p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <p className="border-t pt-2 text-xs text-muted-foreground">
                      CREADO POR: {sel.medico} — {ev.created_at ? new Date(ev.created_at).toLocaleString(LOCALE) : '—'}
                    </p>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </section>

      {gen === 'receta' && (
        <NuevaReceta pacienteId={pacienteId} diagnosticoInicial={dxPrincipal?.descripcion ?? ''} dxInicial={sel.diagnosticos ?? []}
          onClose={() => setGen(null)} onSaved={() => { setGen(null); toast.success('Receta generada desde la consulta'); refrescar(); }} />
      )}
      {gen === 'pedido' && (
        <NuevaSolicitud pacienteInicial={pacienteId} diagnosticoInicial={dxPrincipal?.descripcion ?? ''}
          onClose={() => setGen(null)} onSaved={() => { setGen(null); toast.success('Pedido de laboratorio generado'); refrescar(); }} />
      )}
      {gen === 'certificado' && (
        <NuevoCertificado pacienteId={pacienteId} diagnosticoInicial={dxPrincipal?.descripcion ?? ''} cie10Inicial={dxPrincipal?.cie10_codigo ?? ''}
          onClose={() => setGen(null)} onSaved={() => { setGen(null); toast.success('Certificado generado desde la consulta'); refrescar(); }} />
      )}
      {curvas && <CurvasCrecimiento pacienteId={pacienteId} nombre={nombre} genero={genero} onClose={() => setCurvas(false)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Vista de solo lectura de una consulta o evolución
// ---------------------------------------------------------------------------
function VistaConsulta({ item, esEvolucion = false, clasif }: {
  item: EvolucionItem; esEvolucion?: boolean;
  clasif: (imc: number | null, fecha?: string | null) => { label: string; cls: string } | null;
}) {
  const ev = item.evolucion as EvPlus;
  const s = item.signos?.[0];
  const imc = s ? calcIMC(s.peso, s.talla) : null;
  const examenes = examenesDe(ev);
  const texto = textoEvolucionDe(ev);
  return (
    <div className="space-y-3 text-sm">
      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-x-6 gap-y-1 rounded-md border px-3 py-2 text-xs">
          <p><b>FC:</b> {s.frecuencia_cardiaca ?? '—'}</p>
          <p><b>FR:</b> {s.frecuencia_resp ?? '—'}</p>
          <p><b>SO2:</b> {s.spo2 ?? '—'}</p>
          <p><b>TA:</b> {s.presion_sistolica != null ? `${s.presion_sistolica}/${s.presion_diastolica ?? '—'}` : '—'}</p>
          <p><b>Temperatura:</b> {s.temperatura ?? '—'}</p>
          <p><b>Peso (kg):</b> {s.peso ?? '—'}</p>
          <p><b>Talla:</b> {s.talla ?? '—'}</p>
          <p><b>IMC:</b> {imc ?? '—'}{(() => { const c = clasif(imc, ev.fecha); return c ? <span className={`ml-1 rounded-full px-1.5 py-0.5 ${c.cls}`}>{c.label}</span> : null; })()}</p>
          <p><b>FUM:</b> {s.fum ? fmtFecha(s.fum) : '—'}</p>
          <p><b>P. cefálico:</b> {s.perimetro_cefalico != null ? `${s.perimetro_cefalico} cm` : '—'}</p>
        </div>
      )}
      {texto && <CampoVer label="Evolución" v={texto} />}
      <CampoVer label={esEvolucion ? 'Subjetivo' : 'Motivo de consulta'} v={esEvolucion ? ev.subjetivo : ev.motivo} />
      {!esEvolucion && <CampoVer label="Subjetivo - Anamnesis" v={ev.subjetivo} />}
      <CampoVer label={esEvolucion ? 'Objetivo' : 'Objetivo - Examen físico'} v={ev.objetivo} />
      {examenes && (
        <div>
          <label className="flex items-center gap-2 text-xs font-semibold uppercase text-muted-foreground">
            <input type="checkbox" checked readOnly className="accent-primary" /> Exámenes complementarios
          </label>
          <p className="mt-1 max-h-48 overflow-y-auto whitespace-pre-wrap rounded-md border px-3 py-2">{examenes}</p>
        </div>
      )}
      <CampoVer label={esEvolucion ? 'Análisis' : 'Análisis - Diagnóstico'} v={ev.analisis} />
      {(item.diagnosticos?.length ?? 0) > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-muted text-xs text-muted-foreground">
              <th className="py-1.5 px-2 text-left">Código</th><th className="py-1.5 px-2 text-left">CIE-10</th>
              <th className="py-1.5 px-2 text-center">Presuntivo</th><th className="py-1.5 px-2 text-center">Definitivo</th>
              <th className="py-1.5 px-2 text-left">Descripción</th>
            </tr></thead>
            <tbody>
              {item.diagnosticos.map((d, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="py-1.5 px-2 font-mono text-primary">{d.cie10_codigo ?? '—'}</td>
                  <td className="py-1.5 px-2 uppercase">{d.descripcion}</td>
                  <td className="py-1.5 px-2 text-center">{d.tipo === 'presuntivo' ? '●' : ''}</td>
                  <td className="py-1.5 px-2 text-center">{d.tipo === 'definitivo' ? '●' : ''}</td>
                  <td className="py-1.5 px-2">{d.es_principal ? 'Principal' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CampoVer label={esEvolucion ? 'Plan' : 'Plan - Tratamiento'} v={ev.plan} />
      <CampoVer label="Observaciones" v={ev.observaciones} />
    </div>
  );
}

function CampoVer({ label, v }: { label: string; v: string | null }) {
  if (!v) return null;
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase text-muted-foreground">{label}</h4>
      <p className="mt-1 whitespace-pre-wrap rounded-md border px-3 py-2">{v}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formulario inline: consulta raíz (SOAP completo) o evolución (texto/SOAP)
// ---------------------------------------------------------------------------
function FormConsulta({ pacienteId, item, episodioId, esEvolucion = false, genero = null, onCancel, onSaved, clasif }: {
  pacienteId: string; item: EvolucionItem | null; episodioId: string | null;
  esEvolucion?: boolean; genero?: string | null; onCancel: () => void; onSaved: (id: string) => void;
  clasif: (imc: number | null, fecha?: string | null) => { label: string; cls: string } | null;
}) {
  const ev = item?.evolucion as EvPlus | undefined;
  const dlg = useDialogoEnLinea();
  const { data: plantillas = [] } = useQuery<Plantilla[]>({ queryKey: ['med-plantillas-sel'], queryFn: () => med.list<Plantilla>('plantilla', '?select=*&deleted_at=is.null&es_activo=is.true&categoria=not.in.(informe,consentimiento_informado,orden_ingreso,protocolo_operatorio,procedimiento_tipo,receta,pedido,cobro)&order=nombre') });
  const { data: convenios = [] } = useQuery<Convenio[]>({ queryKey: ['med-convenios', 'todos'], queryFn: () => medRpc<Convenio[]>('med_convenios', { _tipo: null }), enabled: !esEvolucion });

  const [plantillaId, setPlantillaId] = useState(ev?.plantilla_id ?? '');
  const [convenioId, setConvenioId] = useState(ev?.convenio_id ?? '');
  const [soap, setSoap] = useState({
    motivo: ev?.motivo ?? '', subjetivo: ev?.subjetivo ?? '', objetivo: ev?.objetivo ?? '',
    analisis: ev?.analisis ?? '', plan: ev?.plan ?? '', observaciones: ev?.observaciones ?? '',
  });
  const [texto, setTexto] = useState(() => (ev ? textoEvolucionDe(ev) : ''));
  const [modoSoap, setModoSoap] = useState(() => (esEvolucion
    ? !!(ev && (ev.subjetivo || ev.objetivo || ev.analisis || ev.plan)) : true));
  const [exCheck, setExCheck] = useState(() => !!(ev && examenesDe(ev)));
  const [examenes, setExamenes] = useState(() => (ev ? examenesDe(ev) : ''));
  const [signos, setSignos] = useState<SignoVital>(item?.signos?.[0] ?? sigVacio);
  const [dx, setDx] = useState<Diagnostico[]>(item?.diagnosticos?.map((d) => ({
    cie10_codigo: d.cie10_codigo, descripcion: d.descripcion, tipo: d.tipo, es_principal: d.es_principal,
  })) ?? []);
  const [datos, setDatos] = useState<Record<string, unknown>>(() => {
    const d = { ...(ev?.datos ?? {}) };
    delete d.examenes_complementarios; delete d.notas_seguimiento; delete d.texto_evolucion;
    return d;
  });

  const plantillaSel = plantillas.find((p) => p.id === plantillaId);
  const formato = !esEvolucion && plantillaSel && esSchema(plantillaSel.contenido) ? plantillaSel.contenido : null;

  // ---- Asistente de voz (Fase 2): declarar el formulario activo para el dictado estructurado ----
  const camposVoz = useMemo<CampoVoz[]>(() => {
    if (formato) {
      return (formato.secciones ?? [])
        .flatMap((s) => s.campos)
        .filter((c) => c.tipo !== 'titulo' && c.clave !== 'imc')
        .map((c) => ({ clave: c.clave, etiqueta: c.etiqueta, tipo: c.tipo as CampoVoz['tipo'], opciones: c.opciones, unidad: c.unidad }));
    }
    const vitales: CampoVoz[] = [
      { clave: 'presion_sistolica', etiqueta: 'Presión sistólica', tipo: 'numero', unidad: 'mmHg' },
      { clave: 'presion_diastolica', etiqueta: 'Presión diastólica', tipo: 'numero', unidad: 'mmHg' },
      { clave: 'frecuencia_cardiaca', etiqueta: 'Frecuencia cardíaca', tipo: 'numero', unidad: 'lpm' },
      { clave: 'frecuencia_resp', etiqueta: 'Frecuencia respiratoria', tipo: 'numero', unidad: 'rpm' },
      { clave: 'spo2', etiqueta: 'Saturación de oxígeno', tipo: 'numero', unidad: '%' },
      { clave: 'temperatura', etiqueta: 'Temperatura', tipo: 'numero', unidad: '°C' },
      { clave: 'peso', etiqueta: 'Peso', tipo: 'numero', unidad: 'kg' },
      { clave: 'talla', etiqueta: 'Talla', tipo: 'numero', unidad: 'cm' },
      { clave: 'perimetro_cefalico', etiqueta: 'Perímetro cefálico', tipo: 'numero', unidad: 'cm' },
      // 691: FUM solo si el paciente no es masculino
      ...(genero !== 'M' ? [{ clave: 'fum', etiqueta: 'Fecha de última menstruación (FUM)', tipo: 'fecha' as const }] : []),
    ];
    const comunes: CampoVoz[] = [
      { clave: 'subjetivo', etiqueta: 'Subjetivo - Anamnesis', tipo: 'textarea' },
      { clave: 'objetivo', etiqueta: 'Objetivo - Examen físico', tipo: 'textarea' },
      { clave: 'analisis', etiqueta: 'Análisis - Diagnóstico', tipo: 'textarea' },
      { clave: 'plan', etiqueta: 'Plan - Tratamiento', tipo: 'textarea' },
      { clave: 'observaciones', etiqueta: 'Observaciones', tipo: 'textarea' },
      { clave: 'examenes_complementarios', etiqueta: 'Exámenes complementarios', tipo: 'textarea' },
    ];
    return esEvolucion
      ? [{ clave: 'texto_evolucion', etiqueta: 'Evolución', tipo: 'textarea' }, ...comunes, ...vitales]
      : [{ clave: 'motivo', etiqueta: 'Motivo de consulta', tipo: 'textarea' }, ...comunes, ...vitales];
  }, [formato, esEvolucion, genero]);

  const SOAP_VOZ = ['motivo', 'subjetivo', 'objetivo', 'analisis', 'plan', 'observaciones'];
  useVoiceForm({
    modulo: formato ? `formato: ${plantillaSel?.nombre ?? ''}` : esEvolucion ? 'evolución' : 'consulta',
    pacienteId,
    campos: camposVoz,
    aplicar: (v) => {
      if (formato) {
        setDatos((prev) => {
          const next = { ...prev, ...v };
          const imc = calcIMC(next.peso, next.talla);
          if (imc != null && camposVoz.some((c) => c.clave === 'peso')) next.imc = imc;
          return next;
        });
        return;
      }
      const soapPatch: Record<string, string> = {};
      const signosPatch: Partial<SignoVital> = {};
      for (const [k, val] of Object.entries(v)) {
        if (SOAP_VOZ.includes(k)) soapPatch[k] = String(val);
        else if (k === 'examenes_complementarios') { setExCheck(true); setExamenes(String(val)); }
        else if (k === 'texto_evolucion') setTexto(String(val));
        else if (k === 'fum') signosPatch.fum = String(val);
        else (signosPatch as Record<string, unknown>)[k] = val === '' || val == null ? null : Number(val);
      }
      if (Object.keys(soapPatch).length) setSoap((s) => ({ ...s, ...soapPatch }));
      if (Object.keys(signosPatch).length) setSignos((s) => ({ ...s, ...signosPatch }));
      if (esEvolucion && (soapPatch.subjetivo || soapPatch.objetivo || soapPatch.analisis || soapPatch.plan)) setModoSoap(true);
    },
  });

  const aplicarPlantilla = (id: string) => {
    setPlantillaId(id);
    const c = plantillas.find((p) => p.id === id)?.contenido;
    if (!c) return;
    if (esSchema(c)) { setDatos({}); return; }
    const sc = c as SoapContenido;
    setSoap((s) => ({ ...s, motivo: sc.motivo ?? s.motivo, subjetivo: sc.subjetivo ?? s.subjetivo, objetivo: sc.objetivo ?? s.objetivo, analisis: sc.analisis ?? s.analisis, plan: sc.plan ?? s.plan }));
  };

  const sigNum = (k: keyof SignoVital, v: string) => setSignos((s) => ({ ...s, [k]: v === '' ? null : Number(v) }));
  const sigVal = (k: keyof SignoVital) => { const v = signos[k]; return v == null ? '' : String(v); };
  const imc = calcIMC(signos.peso, signos.talla);

  const guardarPlantilla = useMutation({
    mutationFn: (nombrePl: string) => {
      const contenido: SoapContenido = {};
      (['motivo', 'subjetivo', 'objetivo', 'analisis', 'plan'] as const).forEach((k) => { if (soap[k].trim()) contenido[k] = soap[k]; });
      const actualiza = plantillaSel && !formato && nombrePl.trim() === plantillaSel.nombre;
      return medRpc('med_plantilla_guardar', { _data: { id: actualiza ? plantillaSel.id : undefined, nombre: nombrePl.trim(), contenido } });
    },
    onSuccess: () => toast.success('Plantilla guardada'),
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const pedirNombrePlantilla = async () => {
    const v = await dlg.pedir(
      plantillaSel && !formato ? `Actualizar o renombrar la plantilla "${plantillaSel.nombre}"` : 'Nombre de la plantilla',
      [{ key: 'nombre', label: 'Nombre de la plantilla', valor: plantillaSel?.nombre ?? '' }],
    );
    if (v === null) return;
    guardarPlantilla.mutate(v.nombre);
  };

  const guardar = useMutation({
    mutationFn: async (firmar: boolean) => {
      const errSig = validarSignos(signos);
      if (errSig) throw new Error(errSig);
      const datosMerged: Record<string, unknown> = { ...(formato ? datos : {}) };
      if (exCheck && examenes.trim()) datosMerged.examenes_complementarios = examenes;
      if (esEvolucion && texto.trim()) datosMerged.texto_evolucion = texto;
      const notasPrev = ev ? notasDe(ev) : [];
      if (notasPrev.length) datosMerged.notas_seguimiento = notasPrev;
      const soapFinal = esEvolucion && !modoSoap
        ? { motivo: '', subjetivo: '', objetivo: '', analisis: '', plan: '', observaciones: soap.observaciones }
        : soap;
      const r = await medRpc<{ id: string }>('med_guardar_evolucion', {
        _data: {
          id: ev?.id, paciente_id: pacienteId,
          episodio_id: ev ? undefined : episodioId ?? undefined,
          plantilla_id: plantillaId || null,
          convenio_id: convenioId || null,
          ...soapFinal, datos: datosMerged, signos: formato ? sigVacio : signos, diagnosticos: dx,
        },
      });
      if (firmar) await medRpc('med_firmar_evolucion', { _id: r.id });
      return r;
    },
    onSuccess: (r) => { toast.success(esEvolucion ? 'Evolución guardada' : 'Consulta guardada'); onSaved(r.id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const lbl = 'text-xs font-semibold uppercase text-muted-foreground';
  return (
    <div className="space-y-3 text-sm">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <p className="font-semibold">{esEvolucion ? (ev ? 'Editar evolución' : 'EVOLUCIÓN (nueva)') : ev ? 'Editar consulta' : 'Nueva consulta'}</p>
        {!esEvolucion && (
          <select className={`${fieldCls} w-auto min-w-[220px]`} value={plantillaId} onChange={(e) => aplicarPlantilla(e.target.value)} title="Utilizar una plantilla">
            <option value="">Utilizar una plantilla…</option>
            {plantillas.map((p) => <option key={p.id} value={p.id}>{p.codigo ? `${p.codigo} · ` : ''}{p.nombre}</option>)}
          </select>
        )}
      </div>

      {!esEvolucion && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Campo label="Convenio (empresa/aseguradora)">
            <select className={fieldCls} value={convenioId} onChange={(e) => setConvenioId(e.target.value)}>
              <option value="">— Ninguno —</option>{convenios.map((c) => <option key={c.id} value={c.id}>{c.razon_social} ({c.tipo})</option>)}
            </select>
          </Campo>
        </div>
      )}

      {formato ? (
        <FormularioClinico schema={formato} valores={datos} onChange={setDatos} />
      ) : (<>
        {/* Signos vitales (con FUM y perímetro cefálico) */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <Campo label="FC"><input type="number" className={fieldCls} value={sigVal('frecuencia_cardiaca')} onChange={(e) => sigNum('frecuencia_cardiaca', e.target.value)} /></Campo>
          <Campo label="FR"><input type="number" className={fieldCls} value={sigVal('frecuencia_resp')} onChange={(e) => sigNum('frecuencia_resp', e.target.value)} /></Campo>
          <Campo label="SO2"><input type="number" className={fieldCls} value={sigVal('spo2')} onChange={(e) => sigNum('spo2', e.target.value)} /></Campo>
          <Campo label="TA (sist/diast)">
            <div className="flex gap-1">
              <input type="number" className={fieldCls} placeholder="120" value={sigVal('presion_sistolica')} onChange={(e) => sigNum('presion_sistolica', e.target.value)} />
              <input type="number" className={fieldCls} placeholder="80" value={sigVal('presion_diastolica')} onChange={(e) => sigNum('presion_diastolica', e.target.value)} />
            </div>
          </Campo>
          <Campo label="Temperatura"><input type="number" step="0.1" className={fieldCls} value={sigVal('temperatura')} onChange={(e) => sigNum('temperatura', e.target.value)} /></Campo>
          <Campo label="Peso (kg)"><input type="number" step="0.1" max={300} className={fieldCls} value={sigVal('peso')} onChange={(e) => sigNum('peso', e.target.value)} /></Campo>
          <Campo label="Talla (cm)"><input type="number" step="0.1" max={250} className={fieldCls} value={sigVal('talla')} onChange={(e) => sigNum('talla', e.target.value)} /></Campo>
          <Campo label="IMC"><input readOnly tabIndex={-1} className={`${fieldCls} bg-muted/50`} value={imc ?? ''} placeholder="—" /></Campo>
          {genero !== 'M' && <Campo label="FUM"><input type="date" className={fieldCls} value={signos.fum ?? ''} onChange={(e) => setSignos((s) => ({ ...s, fum: e.target.value || null }))} /></Campo>}
          <Campo label="P. cefálico (cm)"><input type="number" step="0.1" className={fieldCls} value={sigVal('perimetro_cefalico')} onChange={(e) => sigNum('perimetro_cefalico', e.target.value)} /></Campo>
        </div>
        {(() => { const c = clasif(imc); return c ? <p className="text-xs text-muted-foreground">IMC {imc} kg/m² · <span className={`rounded-full px-2 py-0.5 ${c.cls}`}>{c.label}</span></p> : null; })()}
        {validarSignos(signos) && <p className="text-xs text-red-500">{validarSignos(signos)}</p>}

        {esEvolucion ? (<>
          <div>
            <span className={lbl}>Evolución</span>
            <AutoTextarea minRows={3} className="mt-1" value={texto} onChange={(e) => setTexto(e.target.value)} />
            <label className="mt-1 flex items-center gap-2 text-xs text-primary cursor-pointer">
              <input type="checkbox" className="accent-primary" checked={modoSoap} onChange={(e) => setModoSoap(e.target.checked)} /> EVOLUCIÓN SOAP
            </label>
          </div>
          {modoSoap && (
            <div className="grid grid-cols-2 gap-3">
              <div><span className={lbl}>Subjetivo</span><AutoTextarea className="mt-1" value={soap.subjetivo} onChange={(e) => setSoap({ ...soap, subjetivo: e.target.value })} /></div>
              <div><span className={lbl}>Objetivo</span><AutoTextarea className="mt-1" value={soap.objetivo} onChange={(e) => setSoap({ ...soap, objetivo: e.target.value })} /></div>
              <div><span className={lbl}>Análisis</span><AutoTextarea className="mt-1" value={soap.analisis} onChange={(e) => setSoap({ ...soap, analisis: e.target.value })} /></div>
              <div><span className={lbl}>Plan</span><AutoTextarea className="mt-1" value={soap.plan} onChange={(e) => setSoap({ ...soap, plan: e.target.value })} /></div>
            </div>
          )}
        </>) : (<>
          <div>
            <span className={lbl}>Motivo de consulta</span>
            <AutoTextarea className="mt-1" value={soap.motivo} onChange={(e) => setSoap({ ...soap, motivo: e.target.value })} />
          </div>
          <div>
            <span className={lbl}>Subjetivo - Anamnesis</span>
            <AutoTextarea className="mt-1" value={soap.subjetivo} onChange={(e) => setSoap({ ...soap, subjetivo: e.target.value })} />
          </div>
          <div>
            <span className={lbl}>Objetivo - Examen físico</span>
            <AutoTextarea className="mt-1" value={soap.objetivo} onChange={(e) => setSoap({ ...soap, objetivo: e.target.value })} />
          </div>
          <div>
            <span className={lbl}>Análisis - Diagnóstico</span>
            <AutoTextarea className="mt-1" value={soap.analisis} onChange={(e) => setSoap({ ...soap, analisis: e.target.value })} />
          </div>
        </>)}

        <label className="flex items-center gap-2 text-xs text-primary cursor-pointer">
          <input type="checkbox" className="accent-primary" checked={exCheck} onChange={(e) => setExCheck(e.target.checked)} /> Exámenes complementarios
        </label>
        {exCheck && <AutoTextarea minRows={4} placeholder="Pega o transcribe el informe del examen…" value={examenes} onChange={(e) => setExamenes(e.target.value)} />}

        <DiagnosticosCie dx={dx} setDx={setDx} />

        {!esEvolucion && (
          <div>
            <span className={lbl}>Plan - Tratamiento</span>
            <AutoTextarea className="mt-1" value={soap.plan} onChange={(e) => setSoap({ ...soap, plan: e.target.value })} />
          </div>
        )}
        <div>
          <span className={lbl}>Observaciones</span>
          <AutoTextarea className="mt-1" value={soap.observaciones} onChange={(e) => setSoap({ ...soap, observaciones: e.target.value })} />
        </div>
      </>)}

      <div className="flex justify-end gap-2 border-t pt-3">
        <Button variant="outline" size="sm" onClick={onCancel}>Cancelar</Button>
        {!formato && !esEvolucion && (
          <Button variant="outline" size="sm" isLoading={guardarPlantilla.isPending} onClick={() => void pedirNombrePlantilla()}>
            💾 {plantillaSel ? 'Actualizar plantilla' : 'Guardar plantilla'}
          </Button>
        )}
        <Button variant="outline" size="sm" isLoading={guardar.isPending} onClick={() => guardar.mutate(false)}>Guardar</Button>
        <Button size="sm" isLoading={guardar.isPending} onClick={() => guardar.mutate(true)}><FileSignature className="w-4 h-4 mr-1" /> Guardar y firmar</Button>
      </div>
    </div>
  );
}
